import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiCalendar, FiLock, FiUnlock, FiPlus, FiAlertTriangle, FiSettings } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  createFinancialYear,
  lockFinancialYear,
  unlockFinancialYear,
} from '../../services/api';
import {
  canEditOpeningBalanceYear,
  sortFinancialYearsAsc,
  isCurrentFinancialYear,
  isFutureFinancialYear,
  isPastFinancialYear,
} from '../../utils/financialYears';
import { formatDate } from '../../utils/dateUtils';
import styles from './Accounting.module.css';

const getFyStartYear = (fy) => parseInt(fy.startDate.slice(0, 4), 10);

const FinancialYearsPage = () => {
  const {
    activeDerasarId,
    financialYears,
    financialYearsLoaded,
    refreshFinancialYears,
    can,
  } = useAuth();
  const navigate = useNavigate();

  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [saving, setSaving] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const data = await refreshFinancialYears(activeDerasarId);
      setYears(data);
    } catch {
      toast.error('Failed to load financial years');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, refreshFinancialYears]);

  useEffect(() => {
    if (!activeDerasarId) return;
    if (financialYearsLoaded) {
      setYears(financialYears);
      setLoading(false);
      return;
    }
    load();
  }, [activeDerasarId, financialYears, financialYearsLoaded, load]);

  const sortedYears = sortFinancialYearsAsc(years);
  const earliestYear = sortedYears[0] || null;
  const latestYear = sortedYears[sortedYears.length - 1] || null;
  // OB lives on the first FY (earliest startDate). No more phantom Opening year.
  const obSetupYear = (earliestYear && canEditOpeningBalanceYear(earliestYear, years)) ? earliestYear : null;

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createFinancialYear({ derasarId: activeDerasarId, startYear: parseInt(startYear, 10) });
      toast.success('Financial year created');
      setShowModal(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create financial year');
    } finally {
      setSaving(false);
    }
  };

  const handleLockToggle = async (financialYear, shouldLock) => {
    const actionLabel = shouldLock ? 'lock' : 'unlock';
    if (!window.confirm(`${shouldLock ? 'Lock' : 'Unlock'} ${financialYear.yearLabel}?`)) return;

    setProcessingId(financialYear.id);
    try {
      const { data } = shouldLock
        ? await lockFinancialYear(financialYear.id)
        : await unlockFinancialYear(financialYear.id);
      toast.success(data.message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${actionLabel} year`);
    } finally {
      setProcessingId(null);
    }
  };

  const statusBadge = (financialYear) => {
    if (isCurrentFinancialYear(financialYear)) {
      return <span className="badge badge-approved">Current</span>;
    }
    if (isFutureFinancialYear(financialYear)) {
      return <span className="badge badge-pending">Future</span>;
    }
    return (
      <span className={`badge ${financialYear.isClosed ? 'badge-rejected' : 'badge-submitted'}`}>
        {financialYear.isClosed ? 'Locked' : 'Unlocked'}
      </span>
    );
  };


  const newYearStartYear = latestYear ? getFyStartYear(latestYear) + 1 : new Date().getFullYear();

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className={`${styles.page} ${styles.pageWide}`}>
      <div className="page-header">
        <h2><FiCalendar /> Financial Years</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {obSetupYear && can('accountingSetup', 'manage') && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(`/accounting/opening-balance/${obSetupYear.id}?mode=setup`)}
            >
              <FiSettings /> Opening Balance Setup
            </button>
          )}
          {can('accountingSetup', 'manage') && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setStartYear(newYearStartYear); setShowModal(true); }}
            >
              <FiPlus /> New Year
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedYears.length === 0 && (
                <tr><td colSpan={5} className="empty-state">No financial years yet. Create one to begin.</td></tr>
              )}

              {[...sortedYears].reverse().map((financialYear) => {
                const isCarryForward = (isPastFinancialYear(financialYear) || isCurrentFinancialYear(financialYear)) && sortedYears.length > 1;
                return (
                  <tr key={financialYear.id}>
                    <td><strong>{financialYear.yearLabel}</strong></td>
                    <td>{formatDate(financialYear.startDate)}</td>
                    <td>{formatDate(financialYear.endDate)}</td>
                    <td>{statusBadge(financialYear)}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => navigate(`/accounting/opening-balance/${financialYear.id}?mode=report`)}
                        >
                          OB Report
                        </button>

                        {isPastFinancialYear(financialYear) && can('accountingSetup', 'manage') && (
                          <button
                            className={`btn btn-sm ${financialYear.isClosed ? 'btn-ghost' : 'btn-danger'}`}
                            onClick={() => handleLockToggle(financialYear, !financialYear.isClosed)}
                            disabled={processingId === financialYear.id}
                          >
                            {financialYear.isClosed ? <FiUnlock /> : <FiLock />}
                            {processingId === financialYear.id
                              ? 'Saving...'
                              : financialYear.isClosed
                                ? 'Unlock Year'
                                : 'Lock Year'}
                          </button>
                        )}

                        {isCurrentFinancialYear(financialYear) && (
                          <span className={styles.frozenNote}><FiAlertTriangle /> Current year cannot be locked</span>
                        )}

                        {isFutureFinancialYear(financialYear) && (
                          <span className={styles.frozenNote}><FiCalendar /> Future year ready for setup</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="card-header">
              <h3><FiPlus /> New Financial Year</h3>
            </div>
            <form onSubmit={handleCreate}>
              <div className={styles.modalBody}>
                <div className={`${styles.infoBox} ${styles.infoBoxInfo}`}>
                  <FiCalendar />
                  <span>
                    Creates the next year after your latest year ({latestYear ? latestYear.yearLabel : '—'}).
                    Historical balances are entered as the Opening Balance on the first year — past years cannot be added separately.
                  </span>
                </div>
                <div className="form-group">
                  <label>Start Year (e.g. 2026 for 2026-27)</label>
                  <input
                    type="number"
                    min="2000"
                    max="2099"
                    value={startYear}
                    onChange={(event) => setStartYear(event.target.value)}
                    required
                  />
                  <small>Year will run from April 1, {startYear} to March 31, {parseInt(startYear, 10) + 1}</small>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Year'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialYearsPage;
